import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const Shop = () => {
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(category || 'all');
  const { cart, setCart } = useContext(AuthContext);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const fetchProducts = async () => {
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/products` 
        : `${API}/products?category=${selectedCategory}`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      setCart(cart.map(item => 
        item.product_id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { product_id: product.id, quantity: 1, product }]);
    }
    toast.success('Added to cart');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-5xl font-bold text-center mb-4 bg-gradient-to-r from-teal-500 to-pink-500 bg-clip-text text-transparent">
          Shop Our Collection
        </h1>
        <p className="text-center text-gray-600 mb-12">Discover unique pieces that speak to your soul</p>
        
        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12" data-testid="category-filter">
          <Button
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            onClick={() => setSelectedCategory('all')}
            className="rounded-full"
            data-testid="filter-all"
          >
            All
          </Button>
          <Button
            variant={selectedCategory === 'fluid_art' ? 'default' : 'outline'}
            onClick={() => setSelectedCategory('fluid_art')}
            className="rounded-full"
            data-testid="filter-fluid-art"
          >
            Fluid Art
          </Button>
          <Button
            variant={selectedCategory === 'photography_prints' ? 'default' : 'outline'}
            onClick={() => setSelectedCategory('photography_prints')}
            className="rounded-full"
            data-testid="filter-photography"
          >
            Photography Prints
          </Button>
        </div>
        
        {/* Products Grid */}
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="products-grid">
          {products.map((product) => (
            <Card key={product.id} className="overflow-hidden hover:shadow-xl transition-shadow" data-testid={`product-${product.id}`}>
              <div className="aspect-square overflow-hidden">
                <img 
                  src={product.image_url}
                  alt={product.title}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-2" data-testid="product-title">{product.title}</h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-teal-600" data-testid="product-price">${product.price.toFixed(2)}</span>
                  <Button 
                    onClick={() => addToCart(product)}
                    className="bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 rounded-full"
                    data-testid="add-to-cart-btn"
                  >
                    Add to Cart
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {products.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No products found in this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;